<template>
  <NoAccessView v-if="isDemographicSurveyReportRestricted" />
  <NoDataForWidget v-else-if="isNoData && !pageLoading" />
  <div v-else class="d-flex flex-column demo-survey-report">
    <InsightHeader
      :loading="pageLoading"
      :reporting-message="reportingMessage"
      @close="setReportingMessage({ header: '', message: '', beaconArticleId: '' })"
    />
    <div v-if="pageLoading" class="gender-race-completion ga-4 mb-4">
      <CompletionChartLoader class="completion-chart" />
      <CompletionChartLoader class="completion-chart" />
    </div>
    <div v-else class="gender-race-completion ga-4 mb-4">
      <GenderCompletionChart class="completion-chart" />
      <RaceCompletionChart class="completion-chart" />
    </div>
    <SwitchLoader v-if="pageLoading" class="mb-4" />
    <DpplSwitch
      v-else
      left="Survey Insights"
      right="Distribution Insights"
      :value="isSurveyInsightsTab"
      :width="320"
      class="mb-4"
      new-version
      :disabled="isGated"
      @change="updateTab"
    />
    <SurveyInsights
      v-if="isSurveyInsightsTab"
      :is-low-data="isLowData"
      :can-demographic-survey-report="authData.canDemographicSurveyReport"
      :opted-to-view-data-anyway="optedToViewDataAnyway"
      :loading="pageLoading"
      @update:view-data-anyway="optedToViewDataAnyway = true"
      @update:is-survey-insights-tab="updateTab"
    />
    <DistributionInsights
      v-else
      :loading="pageLoading"
      :req-fill-rate-loading="reqFillRateLoading"
    />
    <FilterExportPreview class="mt-4" />
  </div>
</template>

<script setup>
import {
  onMounted, onBeforeUnmount, computed, watch, ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import { useDatePicker } from '@/stores/filters/datePicker';
import { useFilters } from '@/stores/filters/filters';
import { useAppBase } from '@/stores/app/base';
import { storeToRefs } from 'pinia';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppLoading } from '@/stores/app/loading';
import DpplSwitch from '@/components/datapeople/DpplSwitch.vue';
import SurveyInsights from '@/components/insights/ProcessInsights/DemographicSurveyReport/SurveyInsights.vue';
import DistributionInsights from '@/components/insights/ProcessInsights/DemographicSurveyReport/DistributionInsights.vue';
import NoAccessView from '@/components/insights/ProcessInsights/DemographicSurveyReport/NoAccessView.vue';
import GenderCompletionChart from '@/components/insights/ProcessInsights/DemographicSurveyReport/charts/GenderCompletionChart.vue';
import RaceCompletionChart from '@/components/insights/ProcessInsights/DemographicSurveyReport/charts/RaceCompletionChart.vue';
import { useDemographicSurveyReport } from '@/stores/insights/demographicSurveyReport';
import { useInsights } from '@/stores/insights/insights';
import InsightHeader from '@/components/insights/InsightHeader.vue';
import SwitchLoader from '@/components/insights/ProcessInsights/DemographicSurveyReport/skeletonLoaders/SwitchLoader.vue';
import CompletionChartLoader from '@/components/insights/ProcessInsights/DemographicSurveyReport/skeletonLoaders/CompletionChartLoader.vue';
import NoDataForWidget from '@/components/insights/NoDataForWidget.vue';
import { useInsightsExport } from '@/stores/insights/export';
import { useTracking } from '@/stores/tracking/tracking';
import { useInsightsShare } from '@/stores/insights/share';
import FilterExportPreview from '@/components/insights/ExportModal/FilterExportPreview.vue';

const route = useRoute();
const router = useRouter();

const {
  loadDemographicSurveyReport,
  fetchReqFillRate,
  updateIsSurveyInsightsTab,
  downloadReqFillRateCSV,
  downloadSourceTableCSV,
  downloadGenderTableCSV,
  downloadRaceTableCSV,
  updateViaQueryParams,
} = useDemographicSurveyReport();
const { showFilterBarSettings } = useAppBase();
const { setFilterConfig, loadFilters } = useFilters();
const { setDatePickerVariant } = useDatePicker();
const { setReportingMessage } = useInsights();
const { setExportConfig, resetExportConfig } = useInsightsExport();
const { setShareConfig, resetShareConfig } = useInsightsShare();

const { reportingMessage } = storeToRefs(useInsights());
const { pageLoading } = storeToRefs(useAppLoading());
const { authData, isDemographicSurveyReportRestricted } = storeToRefs(useAppAuthData());
const {
  genderBreakdown,
  raceBreakdown,
  isSurveyInsightsTab,
  reqFillRateLoading,
  isNoData,
  isTableGenderData,
  reqFillRateSort,
  sourceOpenCategory,
} = storeToRefs(useDemographicSurveyReport());
const { dateRange } = storeToRefs(useDatePicker());

const emits = defineEmits(['track-page-load', 'insights-tracking-event']);

const optedToViewDataAnyway = ref(false);

const isLowData = computed(() => {
  const {
    declined: genderDeclined, male, female, unknown: genderUnknown,
  } = genderBreakdown.value;

  const totalGenderBreakdown = genderDeclined + male + female + genderUnknown;
  const pctNotSubmittedGenderBreakdown = !genderUnknown
    ? 0
    : ((genderUnknown + genderDeclined) / totalGenderBreakdown) * 100;

  const {
    asian,
    black,
    declined: raceDeclined,
    hispanic_latino: hispanicLatino,
    native_american: nativeAmerican,
    pacific_islander: pacificIslander,
    two_or_more: twoOrMore,
    unknown: raceUnknown,
    white,
  } = raceBreakdown.value;

  const totalRaceBreakdown = asian
    + black
    + raceDeclined
    + hispanicLatino
    + nativeAmerican
    + pacificIslander
    + twoOrMore
    + raceUnknown
    + white;
  const pctNotSubmittedRaceBreakdown = !raceUnknown
    ? 0
    : ((raceUnknown + raceDeclined) / totalRaceBreakdown) * 100;

  if (
    pctNotSubmittedGenderBreakdown >= 30
    || pctNotSubmittedRaceBreakdown >= 30
  ) {
    return true;
  }

  return false;
});

const isDefaultToDistributionInsights = computed(() => isLowData.value
  || !authData.value.canDemographicSurveyReport);

const isGated = computed(() => isSurveyInsightsTab.value
  && ((isLowData.value && !optedToViewDataAnyway.value)
  || (!authData.value.canDemographicSurveyReport)));

const dsrShareConfig = computed(() => {
  if (isSurveyInsightsTab.value) {
    return {
      shareable: true,
      showReportImageButton: true,
      checkDSRAccess: true,
    };
  }

  return {
    shareable: true,
    showReportImageButton: false,
    checkDSRAccess: true,
  };
});

const dsrExportConfig = computed(() => {
  if (isSurveyInsightsTab.value) {
    const pngWidgetOptions = {
      'gender-survey-completion': 'Graph: Submission Rate for Gender',
      'race-survey-completion': 'Graph: Submission Rate for Race',
      'gender-distribution': 'Graph: Gender Distribution of Apps',
      'gender-by-stage': 'Graph: Gender Distribution by Stage',
      'race-distribution': 'Graph: Racial Distribution of Apps',
      'race-by-stage': 'Graph: Racial Distribution by Stage',
      'gender-data-table': 'Table: Gender Data',
      'race-data-table': 'Table: Race Data',
    };

    if (isTableGenderData.value) {
      delete pngWidgetOptions['race-data-table'];
    } else {
      delete pngWidgetOptions['gender-data-table'];
    }

    return {
      csvExport: true,
      pngExport: true,
      pngTrackingEventFn: () => {
        useTracking().insightsDownloadReport({
          reportName: route.name,
          downloadType: 'Full Report',
          downloadLocation: 'Download Modal',
          fileType: 'PNG',
        });
      },
      pngWidgetOptions,
      csvOptions: [
        {
          label: 'Gender table',
          csvDownloadFunction: () => downloadGenderTableCSV({
            projectName: authData.value.projectName,
            dateRange: dateRange.value,
          }),
          csvTrackingEventFn: () => {
            useTracking().insightsDownloadReport({
              reportName: route.name,
              downloadType: 'Individual Widgets',
              downloadLocation: 'Download Modal',
              fileType: 'CSV',
              widgetName: 'Gender table',
            });
          },
        },
        {
          label: 'Race table',
          csvDownloadFunction: () => downloadRaceTableCSV({
            projectName: authData.value.projectName,
            dateRange: dateRange.value,
          }),
          csvTrackingEventFn: () => {
            useTracking().insightsDownloadReport({
              reportName: route.name,
              downloadType: 'Individual Widgets',
              downloadLocation: 'Download Modal',
              fileType: 'CSV',
              widgetName: 'Race table',
            });
          },
        },
      ],
    };
  }

  return {
    csvExport: true,
    pngExport: false,
    pngWidgetOptions: {},
    csvOptions: [
      {
        label: 'Source Data Table (.csv)',
        csvDownloadFunction: () => downloadSourceTableCSV({
          projectName: authData.value.projectName,
          dateRange: dateRange.value,
        }),
        csvTrackingEventFn: () => {
          useTracking().insightsDownloadReport({
            reportName: route.name,
            downloadType: 'Individual Widgets',
            downloadLocation: 'Download Modal',
            fileType: 'CSV',
            widgetName: 'Source Data Table',
          });
        },
      },
      {
        label: 'Requisition Data Table (.csv)',
        csvDownloadFunction: async () => downloadReqFillRateCSV({
          projectName: authData.value.projectName,
          dateRange: dateRange.value,
          routeQuery: route.query,
        }),
        csvTrackingEventFn: () => {
          useTracking().insightsDownloadReport({
            reportName: route.name,
            downloadType: 'Individual Widgets',
            downloadLocation: 'Download Modal',
            fileType: 'CSV',
            widgetName: 'Requisition Data Table',
          });
        },
      },
    ],
  };
});

const updateTabQueryParameter = (value) => {
  router.replace({
    query: {
      ...route.query,
      tab: value ? 'survey' : 'distribution',
    },
  }).catch(() => {});
};

const updateTab = (bool) => {
  const trackingLabel = bool
    ? 'Clicked on Survey Insights tab on Demographic-survey-report page'
    : 'Clicked on Distribution Insights tab on Demographic-survey-report page';

  emits('insights-tracking-event', { action: 'trackName', args: { name: trackingLabel } });

  optedToViewDataAnyway.value = false;

  updateIsSurveyInsightsTab(bool);
  updateTabQueryParameter(bool);
};

watch(() => isDefaultToDistributionInsights.value, (defaultToDistribution) => {
  if (defaultToDistribution && !route.query?.tab) {
    updateIsSurveyInsightsTab(false);
    updateTabQueryParameter(false);
  }
}, { immediate: true });

watch([
  () => isTableGenderData.value,
  () => isSurveyInsightsTab.value,
  () => reqFillRateSort.value,
  () => sourceOpenCategory.value,
], ([
  newIsTableGenderData,
  newIsSurveyInsightsTab,
  newReqFillRateSort,
  newSourceOpenCategory,
]) => {
  if (newIsSurveyInsightsTab) {
    router.replace({
      query: {
        ...route.query,
        is_gender_table: newIsTableGenderData || false,
        source_open_category: undefined,
        req_table_sort: undefined,
        tab: 'survey',
      },
    });
  } else {
    router.replace({
      query: {
        ...route.query,
        source_open_category: newSourceOpenCategory || undefined,
        req_table_sort: newReqFillRateSort || undefined,
        is_gender_table: undefined,
        tab: 'distribution',
      },
    });
  }
}, { immediate: true });

const loadReport = (params) => {
  optedToViewDataAnyway.value = false;

  loadDemographicSurveyReport(params);
  fetchReqFillRate(params);
  loadFilters(params);
};

onMounted(() => {
  if (route.query.tab === 'distribution') {
    updateIsSurveyInsightsTab(false);
    updateTabQueryParameter(false);
  } else if (route.query.tab === 'survey') {
    updateIsSurveyInsightsTab(true);
    updateTabQueryParameter(true);
  }

  subscribeToFilterUpdates(loadReport);

  emits('track-page-load', { page: 'Demographic Survey Report' });

  setFilterConfig({
    module: 'insights',
    endpoint: 'demographicSurvey',
    datepicker: true,
    narrow: true,
    showMyJobs: true,
  });

  setDatePickerVariant('multiOption');

  updateViaQueryParams(route.query);

  loadReport({
    routeQuery: route.query,
    router,
  });
});

onBeforeUnmount(() => {
  unsubscribeToFilterUpdates(loadReport);

  resetExportConfig();
  resetShareConfig();
});

watch(() => isDemographicSurveyReportRestricted.value, (value) => {
  if (value) {
    showFilterBarSettings({ route: route.name, options: ['disableFilters', 'hideDatePicker'] });
    resetExportConfig();
    resetShareConfig();
  } else {
    showFilterBarSettings({ route: route.name, options: [] });
    setShareConfig(dsrShareConfig.value);
    setExportConfig(dsrExportConfig.value);
  }
}, { immediate: true });

watch(() => dsrExportConfig.value, (newValue) => {
  if (!isDemographicSurveyReportRestricted.value) {
    setExportConfig(newValue);
  }
}, { deep: true });

watch(() => dsrShareConfig.value, (newValue) => {
  if (!isDemographicSurveyReportRestricted.value) {
    setShareConfig(newValue);
  }
}, { deep: true });
</script>

<style lang="less" scoped>
.gender-race-completion {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  width: 100%;
  min-height: 160px;
}

.completion-chart {
  grid-column: span 1;
}
</style>
